import React, { useState, useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import Swal from "sweetalert2";
import {
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../../data/Axios/DynamicService";
import { HRService } from "../../../../config";
import { PayComponent } from "../../../../data/Queries/HRmanagement/leavemanagement";
import DynamicForm from "../../../../data/Axios/DynamicForm";

const base_url = HRService.uri;

const EmployeePayComponentForm = (props) => {
  const employeeData = JSON.parse(localStorage.getItem("EmployeeData") || "{}");
  const parentEmployeeId = employeeData?.id || null;
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);

  const [payComponentsData, setPayComponentsData] = useState([]);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [loading, setLoading] = useState(true);

  const initialValues = {
    Id: props.data?.Id || "",
    employeeId: props.data?.employeeId || parentEmployeeId || "",
    payComponentId: props.data?.payComponentId || "",
    amount: props.data?.amount || "",
    percentOfBase: props.data?.percentOfBase || "",
    baseComponentCode: props.data?.baseComponentCode || "",
    effectiveFrom: props.data?.effectiveFrom || "",
    effectiveTo: props.data?.effectiveTo || "",
    isActive: props.data?.isActive ?? true,
    notes: props.data?.notes || "",
  };

  // ── FETCH PAY COMPONENTS ───────────────────────────────────────
  useEffect(() => {
    async function fetchPayComponents() {
      try {
        const result = await graphqlQuery(PayComponent, base_url);
        const nonStatutory = result || [];
        setPayComponentsData(nonStatutory);

        // Pre-select when editing
        if (props.data?.payComponentId) {
          const existing = nonStatutory.find(
            (c) => c.id === props.data.payComponentId
          );
          if (existing) setSelectedComponent(existing);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchPayComponents();
  }, [props.data?.payComponentId]);

  const payComponentOptions = payComponentsData.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  const baseComponentOptions = [
    { value: "BASIC", label: "BASIC" },
    { value: "GROSS", label: "GROSS" },
  ];

  // ── SUBMIT ───────────────────────────────────────────────────────
 const handleSubmit = async (values, { setSubmitting }) => {
  try {
    const {
      Id,
      employeeId,
      payComponentId,
      amount,
      percentOfBase,
      baseComponentCode,
      effectiveFrom,
      effectiveTo,
      isActive,
      notes,
    } = values;

    // --- SANITIZE VALUES BEFORE MUTATION ---
    const cleanedData = {
      employeeId: parseInt(employeeId) || null,
      payComponentId: parseInt(payComponentId) || null,
      amount: amount === "" || isNaN(amount) ? null : parseFloat(amount),
      percentOfBase:
        percentOfBase === "" || isNaN(percentOfBase)
          ? null
          : parseFloat(percentOfBase),
      baseComponentCode:
        baseComponentCode && baseComponentCode.trim() !== ""
          ? baseComponentCode
          : null,
      effectiveFrom: effectiveFrom || null,
      effectiveTo: effectiveTo || null,
      isActive: Boolean(isActive),
      notes: notes?.trim() || null,
    };

    // Include createdBy or id depending on mode
    const mutationData = props.isEditing
      ? { id: props.data?.id, ...cleanedData }
      : { ...cleanedData, createdBy: userId };

    await generateAndExecuteMutation(
      props.isEditing
        ? "updateEmployeePayComponent"
        : "createEmployeePayComponent",
      mutationData,
      "employeePayComponent",
      base_url
    );

    props.onAction();
    props.onClose();
    Swal.fire({
      icon: "success",
      title: "Success",
      text: `Pay component ${props.isEditing ? "updated" : "created"}`,
    });
  } catch (err) {
    console.error(err);
    Swal.fire({ icon: "error", title: "Error", text: "Try again later." });
  } finally {
    setSubmitting(false);
  }
};


  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // ── DYNAMIC FIELDS ───────────────────────────────────────────────
  const fields = [
    {
  name: "payComponentId",
  label: "Pay Component *",
  type: "select",
  options: payComponentOptions,
  required: true,
  gridColumn: "span 2",

  // Use getOptionLabel if using Autocomplete-like select
  getOptionLabel: (option) => {
    if (!option) return "";
    // If option is object { value, label }, return label
    if (typeof option === "object" && option.label) return option.label;
    // If option is just ID, find from payComponentsData
    const found = payComponentsData.find((c) => c.id === parseInt(option));
    return found ? found.name : "";
  },

  // Ensure value is compared correctly
  getOptionValue: (option) => {
    return typeof option === "object" ? option.value : option;
  },

  // Critical: onChange must return the correct value format
  onChange: (value, values, setFieldValue) => {
    const id = parseInt(value);
    const selected = payComponentsData.find((c) => c.id === id);

    // Update form value (ID)
    setFieldValue("payComponentId", id);

    // Update local state
    setSelectedComponent(selected || null);

    // Reset dependent fields
    setFieldValue("amount", "");
    setFieldValue("percentOfBase", "");
    setFieldValue("baseComponentCode", "");

    // Auto-fill base for percentage-based
    if (selected && selected.calculationBasis === "percentOfBase") {
      setFieldValue("baseComponentCode", "BASIC");
    }
  },
},

    // ── CONDITIONAL FIELDS ───────────────────────────────────────
    ...(selectedComponent?.calculationBasis === "Fixed"
      ? [
          {
            name: "amount",
            label: "Amount *",
            type: "number",
            min: 0,
            step: "0.01",
            required: true,
            gridColumn: "span 1",
          },
        ]
      : ["PercentOfBasic"].includes(
    selectedComponent?.calculationBasis
  )
  ? [
      {
        name: "percentOfBase",
        label: "Percentage of Base (%) *",
        type: "number",
        min: 0,
        max: 500,
        step: "0.01",
        required: true,
        gridColumn: "span 1",
      },
      ...(selectedComponent?.requiresBaseComponent
        ? [
            {
              name: "baseComponentCode",
              label: "Base Component Code",
              type: "select",
              options: baseComponentOptions,
              required: true,
              gridColumn: "span 1",
            },
          ]
        : []),
    ]
  : []
),

    // {
    //   name: "effectiveFrom",
    //   label: "Effective From *",
    //   type: "date",
    //   required: true,
    //   gridColumn: "span 1",
    // },
    {
      name: "effectiveTo",
      label: "Effective To",
      type: "date",
      gridColumn: "span 1",
    },
    {
      name: "notes",
      label: "Notes",
      type: "text",
      gridColumn: "span 4",
      multiline: true,
      rows: 3,
    },
  ];

  const steps = [
    {
      stepLabel: "Employee Pay Component Details",
      fields: fields.map((f) => f.name),
      icon: "payment",
    },
  ];

  const title = props.isEditing
    ? "Edit Employee Pay Component"
    : "Create Employee Pay Component";
  const subtitle = props.isEditing
    ? "Edit an Existing Employee Pay Component"
    : "Create a New Employee Pay Component";

  return (
    <DynamicForm
   //   key={selectedComponent?.id ?? "form"}   // forces re-mount when component changes
      fields={fields}
      steps={steps}
      onSubmit={handleSubmit}
      title={title}
      subtitle={subtitle}
      initialValues={initialValues}
    />
  );
};

export default EmployeePayComponentForm;